import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useSharing, type PermissionLevel } from "@/hooks/use-sharing";
import { 
  Users, 
  Check, 
  X, 
  Eye, 
  Pencil,
  Bell,
  Loader2
} from "lucide-react";

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function PermissionBadge({ level }: { level: PermissionLevel }) {
  if (level === "full") {
    return (
      <Badge variant="default" className="gap-1">
        <Pencil className="h-3 w-3" />
        Acesso total
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1">
      <Eye className="h-3 w-3" />
      Apenas visualizar
    </Badge>
  );
}

interface PendingInvitesModalProps {
  onOpenChange?: (open: boolean) => void;
}

export function PendingInvitesModal({ onOpenChange }: PendingInvitesModalProps) {
  const {
    pendingInvitations,
    pendingAccess,
    loading,
    acceptInvitation,
    acceptShare,
    rejectShare,
    fetchAll,
  } = useSharing();

  const [open, setOpen] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Check for pending invites on mount
  useEffect(() => {
    const hasPending = pendingInvitations.length > 0 || pendingAccess.length > 0;
    if (hasPending && !loading) {
      setOpen(true);
    }
  }, [pendingInvitations.length, pendingAccess.length, loading]);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  const handleAcceptInvitation = async (token: string) => {
    setProcessingId(token);
    await acceptInvitation(token);
    setProcessingId(null);
    await fetchAll();
  };

  const handleAcceptShare = async (shareId: string) => {
    setProcessingId(shareId);
    await acceptShare(shareId);
    setProcessingId(null);
    await fetchAll();
  };

  const handleRejectShare = async (shareId: string) => {
    setProcessingId(shareId);
    await rejectShare(shareId);
    setProcessingId(null);
    await fetchAll();
  };

  const totalPending = pendingInvitations.length + pendingAccess.length;

  if (totalPending === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-yellow-500" />
            Convites pendentes
          </DialogTitle>
          <DialogDescription>
            Você tem {totalPending} convite{totalPending > 1 ? "s" : ""} de compartilhamento pendente{totalPending > 1 ? "s" : ""}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Token-based invitations (from email) */}
          {pendingInvitations.map((inv) => (
            <div key={inv.id}>
              <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={inv.inviter?.avatar_url || undefined} />
                  <AvatarFallback>{getInitials(inv.inviter?.full_name || null)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-2">
                  <div>
                    <p className="text-sm font-medium">
                      {inv.inviter?.full_name || "Alguém"} quer compartilhar a conta com você
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Você terá acesso a todas as informações financeiras
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <PermissionBadge level={inv.permission_level as PermissionLevel} />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() => handleAcceptInvitation(inv.token)}
                      disabled={processingId === inv.token}
                    >
                      {processingId === inv.token ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <Check className="h-4 w-4 mr-1" />
                      )}
                      Aceitar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Direct share requests */}
          {pendingAccess.map((share) => (
            <div key={share.id}>
              <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={share.owner?.avatar_url || undefined} />
                  <AvatarFallback>{getInitials(share.owner?.full_name || null)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-2">
                  <div>
                    <p className="text-sm font-medium">
                      {share.owner?.full_name || "Alguém"} quer compartilhar a conta com você
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Você terá acesso a todas as informações financeiras
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <PermissionBadge level={share.permission_level as PermissionLevel} />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() => handleAcceptShare(share.id)}
                      disabled={processingId === share.id}
                    >
                      {processingId === share.id ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <Check className="h-4 w-4 mr-1" />
                      )}
                      Aceitar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-destructive hover:bg-destructive/10"
                      onClick={() => handleRejectShare(share.id)}
                      disabled={processingId === share.id}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Recusar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Separator />
        
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Ver depois
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Notification badge component to show in the header/sidebar
 */
export function PendingInvitesBadge() {
  const { pendingInvitations, pendingAccess, loading } = useSharing();
  
  const totalPending = pendingInvitations.length + pendingAccess.length;

  if (loading || totalPending === 0) {
    return null;
  }

  return (
    <Badge 
      variant="destructive" 
      className="h-5 w-5 p-0 flex items-center justify-center text-xs rounded-full"
    >
      {totalPending}
    </Badge>
  );
}
